import { format, Locale } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';
import {
  AccountTypes,
  CardSystem,
  UnknownCardSystem,
  PaymentCardItems,
  PaymentDefaultItems,
  ServiceCardIconType,
  LocalStorageItems,
} from '../models/Common';
import { AccountView, View } from '../models/Calculate';
import {
  ExtraServicesMap,
  headerNewsLinkEN,
  headerNewsLinkRU,
  headerNewsLinkUA,
  headerNewsTextEN,
  headerNewsTextRU,
  headerNewsTextUA,
  ppoeSettingsLink,
  suspensionOfServicesIds,
} from '../constants/configuration';
import { BrizTV, ServicesTypes, UnionAccount } from '../models/Accounts';
import { IPermission, IUserRepairWorks } from '../models/UserInfo';
import { CommonShare } from '../models/ServicePresets';

export const getContacts = (): { type: string; contact: string }[] => {
  return [
    { type: 'lifecell', contact: '093 797 25 25' },
    { type: 'vodafone', contact: '095 797 25 25' },
    { type: 'kyivstar', contact: '068 797 25 25' },
    { type: 'email', contact: 'supp@briz.ua' },
  ];
};

export const checkExpiry = () => {
  const itemStr = localStorage.getItem('expiry');
  if (!itemStr) {
    return false;
  }
  const now = new Date();
  return now.getTime() <= +itemStr;
};

export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getObjFromUrlParam = (param: string) => {
  const queryString = window.location.pathname.split('/');
  const searchedElem = queryString.find((el) => el.includes(param)) || '';
  if (searchedElem.includes('=')) {
    const tempArr = searchedElem.split('=');
    return Object.fromEntries(
      tempArr.map((el) => {
        const number = !Number.isNaN(+el) ? +el : 0;
        return [tempArr[0], number];
      }),
    );
  }
  return null;
};

export const getLastUrlSegment = (path: string) => {
  return path.substring(path.lastIndexOf('/') + 1);
};

export const getSearchParamValue = (queryKey: string, path: string) => {
  const objectFromSearchParams = Object.fromEntries(new URLSearchParams(path).entries());
  return objectFromSearchParams[queryKey];
};

export const formatLocale = (locale: string | null | undefined): string => {
  if (locale) {
    return locale === 'ua' ? 'uk' : locale;
  }
  return '';
};

export const getCreditCardType = (card: string): PaymentCardItems | PaymentDefaultItems => {
  const visa = new RegExp('^4[0-9*]{6,}$');
  const masterCard = new RegExp(
    '^5[1-5*][0-9*]{5,}|^222[1-9*][0-9*]{3,}|^22[3-9*][0-9*]{4,}|^2[3-6*][0-9*]{5,}|^27[01][0-9*]{4,}|^2720[0-9*]{3,}$',
  );
  if (visa.test(card)) {
    return CardSystem.VISA;
  }
  if (masterCard.test(card)) {
    return CardSystem.MASTERCARD;
  }
  return UnknownCardSystem.NEW_CARD_DEFAULT;
};

export const formatPhone = (phone: string): string => {
  return phone.replace(/ /g, '');
};

export const formatAccountType = (type: string | undefined) => {
  if (type) {
    switch (true) {
      case type.includes('ctv'):
        return AccountTypes.Ctv;
      case type.includes('iptv'):
        return AccountTypes.Iptv;
      case type.includes('union'):
        return AccountTypes.Union;
      case type.includes('internet'):
        return AccountTypes.Internet;
      default:
        return '';
    }
  }
  return '';
};

export const formatDateAsISO = (days: number, months: number, years: number) => {
  const day = days <= 9 ? `0${days}` : `${days}`;
  const month = months <= 9 ? `0${months}` : `${months}`;
  return `${years}-${month}-${day}`;
};

export const formatDateForCalculation = (date: Date) => {
  const day = date.getDate() <= 9 ? `0${date.getDate()}` : `${date.getDate()}`;
  const month = date.getMonth() + 1 <= 9 ? `0${date.getMonth() + 1}` : `${date.getMonth() + 1}`;
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error != null && 'status' in error;

export const getErrorMessage = (err: unknown): string => {
  if (isFetchBaseQueryError(err)) {
    if (typeof err.data === 'object' && err.data !== null) {
      const data = err.data as { error?: { message?: string }; message?: string };
      return data.error?.message ?? data.message ?? 'Request failed';
    }
    if (typeof err === 'object' && 'error' in err) {
      return err.error;
    }
    return 'Request failed';
  }

  if (err instanceof Error) {
    return err.message;
  }

  if (
    typeof err === 'object' &&
    err !== null &&
    (('message' in err && typeof err.message === 'string') || ('error' in err && typeof err.error === 'string'))
  ) {
    if ('message' in err && typeof err.message === 'string') {
      return err.message;
    }
    if ('error' in err && typeof err.error === 'string') {
      return err.error;
    }
  }

  return 'Unexpected error';
};

export const getCardIconType = (type: AccountTypes | string, serviceType: number | undefined) => {
  switch (true) {
    /* UNIONS */
    case serviceType && (serviceType === ServicesTypes.INTERNET || serviceType === ServicesTypes.INTERNET_DAILY): {
      return ServiceCardIconType.Internet;
    }
    case serviceType && serviceType === ServicesTypes.INTERNET_WITH_IPTV: {
      return ServiceCardIconType.InternetIptv;
    }
    case serviceType && serviceType === ServicesTypes.INTERNET_TV: {
      return ServiceCardIconType.InternetCtv;
    }
    case serviceType && serviceType === ServicesTypes.INTERNET_TV_MAX: {
      return ServiceCardIconType.InternetTvMax;
    }
    /* SEPARATE ACCOUNTS */
    case type === AccountTypes.Internet || type === 'internet_accounts': {
      return ServiceCardIconType.Internet;
    }
    case type === AccountTypes.Ctv || type === 'ctv_accounts': {
      return ServiceCardIconType.Ctv;
    }
    case type === AccountTypes.Iptv || type === 'iptv_accounts': {
      return ServiceCardIconType.Iptv;
    }
    default:
      return ServiceCardIconType.InternetCtv;
  }
};

export const isRepairWorkStarted = (work: IUserRepairWorks) => {
  const start = DateTime.fromFormat(`${work.fromDate} ${work.fromTime ?? '00:00'}`, 'yyyy-MM-dd HH:mm', {
    zone: 'Europe/Kyiv',
  });
  const now = DateTime.now().setZone('Europe/Kyiv');
  return now >= start;
};

export const getRepairProgressPercent = (work: IUserRepairWorks) => {
  const start = DateTime.fromFormat(`${work.fromDate} ${work.fromTime}`, 'yyyy-MM-dd HH:mm', { zone: 'Europe/Kyiv' });
  const end = DateTime.fromFormat(`${work.toDate} ${work.toTime}`, 'yyyy-MM-dd HH:mm', { zone: 'Europe/Kyiv' });
  const now = DateTime.now().setZone('Europe/Kyiv');
  const total = end.toMillis() - start.toMillis();
  const passed = now.toMillis() - start.toMillis();
  if (total <= 0) return 100;
  const percent = (passed / total) * 100;
  return Math.min(100, Math.max(0, Math.round(percent)));
};

export const findViewValuesHelper = (obj: View, key: keyof AccountView) => {
  const service = obj.ctv || obj.inet || obj.iptv || obj.union || obj.hybrid || [];
  if (service.length) {
    return service[0][key];
  }
  return '';
};

export const numWord = (value: number, text: string[]) => {
  const Mathvalue = Math.abs(value) % 100;
  const num = Mathvalue % 10;
  if (Mathvalue > 10 && Mathvalue < 20) return text[0];
  if (num > 1 && num < 5) return text[2];
  if (num === 1) return text[1];
  return text[0];
};

/* возвращает полное имя месяца */
export const getMonthName = (month: number, currentLocale: Locale | undefined) => {
  const date = new Date();
  date.setMonth(month - 1);
  return format(new Date(date), 'LLLL', { locale: currentLocale });
};

/* возвращает номер месяца в формате 01, 02, ..., 12 */
export const getMonthFromString = (month: string): string => {
  if (Number.isNaN(+month)) {
    return '01';
  }
  const date = new Date(2100, +month - 1, 1);
  return format(date, 'LL');
};

/* возвращает день в формате 01, 02, ..., 31 */
export const getNumberOfLastDayOfMonth = (year: string, month: string): string => {
  const date = new Date(+year, +month, 0);
  return format(date, 'dd');
};

export const getPpoeSettingsLink = (locale: string) => {
  return locale !== 'ua' ? `https://www.briz.ua/${locale}/help/pppoe-soedinenie-na-pk` : ppoeSettingsLink;
};

export const phoneNumberWithSpaces = (value: string): string => {
  let i = 0;
  const pattern = '### ### ### ## ##';
  if (value.length === 10) {
    const phone = `+38${value.toString()}`;
    // eslint-disable-next-line no-plusplus
    return pattern.replace(/#/g, () => phone[i++]);
  }
  return value;
};

export const isSystemPaymentAvailable = (availableListUserId: string, userId?: number): boolean => {
  if (availableListUserId === '') return true;
  if (userId) {
    return availableListUserId.split(',').includes(`${userId}`);
  }
  return false;
};

export const headerNewsText = (lang: string) => {
  switch (lang) {
    case 'ru':
      return headerNewsTextRU;
    case 'en':
      return headerNewsTextEN;
    default:
      return headerNewsTextUA;
  }
};

export const headerNewsLink = (lang: string) => {
  switch (lang) {
    case 'ru':
      return headerNewsLinkRU;
    case 'en':
      return headerNewsLinkEN;
    default:
      return headerNewsLinkUA;
  }
};

export const declination = (num: number, txt: string[], cases = [2, 0, 1, 1, 1, 2]) => {
  if (num === 0) {
    return txt[2];
  }
  return txt[num % 100 > 4 && num % 100 < 20 ? 2 : cases[num % 10 < 5 ? num % 10 : 5]];
};

export const getDeclinationValue = (
  value: number | undefined | null,
  locale: string,
  valuesForDeclinationUA: string[],
  valuesForDeclinationRU: string[],
  defaultEnglishValue: string,
): string => {
  switch (locale) {
    case 'ua': {
      return declination(Math.trunc(value || 0), valuesForDeclinationUA);
    }
    case 'ru': {
      return declination(Math.trunc(value || 0), valuesForDeclinationRU);
    }
    default: {
      return defaultEnglishValue;
    }
  }
};

export const getBrizTvCredentials = (accounts?: UnionAccount[] | null): BrizTV | null => {
  if (accounts && 'Iptv' in accounts[0]) {
    return (
      (accounts as UnionAccount[]).filter(
        (el) => el?.Iptv?.BrizTV !== null && !el?.Service?.Daily && !suspensionOfServicesIds.includes(el?.Service?.id),
      )[0]?.Iptv?.BrizTV || null
    );
  }
  return null;
};

/* проверка на наличие ограничений при переходе по приватным роутам */
export const hasPermission = (permissions: IPermission, requiredPermissions: string[] | undefined): boolean => {
  if (!requiredPermissions) {
    return true;
  }

  return requiredPermissions.every((permission) => {
    const permissionLevels = permission.split('.');
    let current = permissions;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < permissionLevels.length; i++) {
      if (!current || !current[permissionLevels[i]]) {
        return false;
      }
      current = current[permissionLevels[i]];
    }

    return true;
  });
};

export const formatNumber = (num: number) => {
  if (Number.isInteger(num)) {
    return num.toString();
  }
  return num.toFixed(2);
};

export const getDeviceId = () => {
  const deviceId = localStorage.getItem(LocalStorageItems.DeviceId);
  const UUID = uuidv4();
  if (!deviceId) {
    localStorage.setItem(LocalStorageItems.DeviceId, UUID);
    return UUID;
  }
  return deviceId;
};

export const getCookie = (name: string) => {
  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`),
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

export const setCookie = (name: string, value: string, age: number) => {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${age};`;
};

export function debounce<T extends (...args: never[]) => void>(func: T, wait: number, immediate = false): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  function debouncedFunction(this: ThisParameterType<T>, ...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };

    const callNow = immediate && !timeout;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(this, args);
  }

  // возвращаем тот же тип, что и входная функция
  return debouncedFunction as T;
}

export const getExtraServiseType = (tags: { id: number; Name: string }[]): keyof typeof ExtraServicesMap | null => {
  const ids = Object.values(ExtraServicesMap) as number[];
  const tagsIds = tags.map((i) => i.id);
  const tagId = ids.find((id) => tagsIds.includes(id));
  if (tagId) {
    return ExtraServicesMap[tagId] as keyof typeof ExtraServicesMap;
  }
  return null;
};

export const checkIsShareAvailableForService = ({
  accountType,
  share,
  serviceId,
}: {
  accountType: AccountTypes | string;
  share: CommonShare;
  serviceId: number;
}): boolean => {
  switch (accountType) {
    case AccountTypes.Union:
      return (
        share.availiable_union_services.includes(serviceId) || !share.notavailiable_union_services.includes(serviceId)
      );
    case AccountTypes.Internet:
      return (
        share.availiable_inet_services.includes(serviceId) || !share.notavailiable_inet_services.includes(serviceId)
      );
    case AccountTypes.Iptv:
      return (
        share.availiable_iptv_services.includes(serviceId) || !share.notavailiable_iptv_services.includes(serviceId)
      );
    case AccountTypes.Ctv:
      return share.availiable_ctv_services.includes(serviceId) || !share.notavailiable_ctv_services.includes(serviceId);

    default:
      return false;
  }
};
